/* eslint-disable import/no-cycle */
/* eslint-disable camelcase */
import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
} from '@reduxjs/toolkit';
import { normalize, schema } from 'normalizr';
import axios from 'axios';
import _cloneDeep from 'lodash/cloneDeep';
import {
  APP,
  WEBINARS_API_URL,
} from 'appenv';
import moment from 'moment';
import * as momentTz from 'moment-timezone';
import findAvailableLocaleByKeys from './helpers/findAvailableLocaleByKeys';

export enum WebinarType {
  Live = 'time_framed',
  OnDemand = 'on_demand'
}

export interface WebinarDay {
  id: number;
  name: string;
  date: string;
}

export interface WebinarSpeaker {
  id: number;
  profile_picture_url: string;
  translations: {
    [locale: string]: {
      name: string;
      company: string;
      title: string;
      bio: string;
    };
  };
}

export interface WebinarTrack {
  id: number;
  color: string;
  track_name: string;
  sort: 2;
  translations: {
    [locale: string]: {
      name: string;
    };
  };
}

export interface Webinar {
  id: number;
  webinar_day: WebinarDay;
  start_time: string;
  startAt: string;
  end_time: string;
  endAt: string;
  timezone: string;
  kind: WebinarType;
  speakers: WebinarSpeaker[];
  track: WebinarTrack;
  sort: number;
  translations: {
    [locale: string]: {
      id: number;
      title: string;
      description: string;
      url: string;
      slido_url: string;
      cloudflare_stream_id: string;
    };
  };
}

/**
 * @description Generates a sort compare function that sorts by:
 * 1. Time
 * 2. Track Priority
 * 3. Webinar Title Locale Compare
 * @param locale: Current Selected Locale
 * @param prev: Previous Webinar
 * @param next: Next Webinar
 */
export const webinarSortFn = (locale: string) => (prev: Webinar, next: Webinar) => {
  // 1. Time
  const prevStartAt = moment(prev?.startAt).valueOf();
  const nextStartAt = moment(next?.startAt).valueOf();
  if (prevStartAt !== nextStartAt) return prevStartAt - nextStartAt;

  // 2: Track Priority
  const prevSort = prev?.track?.sort ?? Number.MAX_SAFE_INTEGER;
  const nextSort = next?.track?.sort ?? Number.MAX_SAFE_INTEGER;
  if (prevSort !== nextSort) return prevSort - nextSort;

  // 3. Webinar Title Locale Compare
  const prevTranslation = prev?.translations?.[locale] ?? ({} as any);
  const nextTranslation = next?.translations?.[locale] ?? ({} as any);
  return prevTranslation?.title?.localeCompare(nextTranslation?.title) ?? 0;
};

export const onDemandWebinarSortFn = (prev: Webinar, next: Webinar) => (prev?.sort ?? Number.MAX_SAFE_INTEGER) - (next?.sort ?? Number.MAX_SAFE_INTEGER);

const translationsToLocaleMap = (translations, keys: string[]) => {
  const remappedLocales = translations.reduce((result, currentValue) => {
    const locale = currentValue.language;
    result[locale] = currentValue;
    return result;
  }, {});
  const remappedLocalesWithFallback = Object.keys(remappedLocales).reduce((acc, locale) => {
    acc[locale] = findAvailableLocaleByKeys(_cloneDeep(remappedLocales), locale, keys);
    return acc;
  }, {});
  return remappedLocalesWithFallback;
};

const WEBINAR_LOCALIZABLE_KEYS = ['description', 'title', 'url', 'slido_url'];
const SPEAKER_LOCALIZABLE_KEYS = ['bio', 'company', 'name', 'title'];
const TRACK_LOCALIZABLE_KEYS = ['name'];

export const webinarEntity = new schema.Entity<Webinar>('webinars', {}, {
  processStrategy: (entity) => {
    entity.translations = translationsToLocaleMap(entity.translations || [{ language: 'en' }], WEBINAR_LOCALIZABLE_KEYS);
    if (APP !== 'hkstp608') {
      entity.speakers = entity.speakers.map((speaker) => speaker.webinar_speaker || [{ language: 'en' }]);
    }
    entity.speakers.forEach((speaker) => {
      // eslint-disable-next-line camelcase
      speaker.avatar = speaker.profile_picture_file?.data?.thumbnails?.find((thumb) => thumb.url.endsWith('thumbnail'))?.url ?? speaker.profile_picture_url;
      speaker.translations = translationsToLocaleMap(speaker.translations || [{ language: 'en' }], SPEAKER_LOCALIZABLE_KEYS);
    });
    if (entity.track != null && entity.track.translations != null) {
      entity.track.translations = translationsToLocaleMap(entity.track.translations, TRACK_LOCALIZABLE_KEYS);
    }
    entity.startAt = entity.webinar_day && entity.start_time
      ? momentTz.tz(`${entity.webinar_day.date}T${entity.start_time}`, entity.timezone).toISOString()
      : null;
    entity.endAt = entity.webinar_day && entity.end_time
      ? momentTz.tz(`${entity.webinar_day.date}T${entity.end_time}`, entity.timezone).toISOString()
      : null;
    return entity;
  },
});

export const fetchWebinars = createAsyncThunk(
  'webinars/fetchAll',
  async (_, { getState }) => {
    try {
      const { eventConfigs, localization } = (getState() as any);
      const { data } = await axios.get(WEBINARS_API_URL, { params: { event_domain: eventConfigs.domain } });
      const result = data;
      result.forEach((entity) => {
        entity.timezone = localization.eventTimezone;
      });
      const normalized = normalize(result, [webinarEntity]);
      return normalized.entities;
    } catch (error) {
      console.error(error);
    }
    return {};
  },
);

// Default Locale of en
const webinarsAdapter = createEntityAdapter<Webinar>({
  sortComparer: webinarSortFn('en'),
});

export const {
  selectEntities: selectWebinarEntities,
  selectAll: selectAllWebinars,
  selectById: selectWebinarById,
} = webinarsAdapter.getSelectors((state: any) => state.webinars);

export const selectAllLiveWebinars = () => createSelector(
  selectAllWebinars,
  (webinars) => webinars.filter((webinar) => (
    webinar.kind === WebinarType.Live
  )),
);

// Tracks are only available for live webinars
export const selectAllRelevantTracks = () => createSelector(
  selectAllWebinars,
  (webinars) => {
    const liveWebinars = webinars.filter((webinar) => webinar.kind === WebinarType.Live);
    const timeMap = {};
    liveWebinars.forEach(({ webinar_day: { date }, start_time, track }) => {
      if (timeMap[`${date}T${start_time}`]) {
        timeMap[`${date}T${start_time}`].push(track);
      } else {
        timeMap[`${date}T${start_time}`] = [track];
      }
    });
    const trackEntities: Record<string, WebinarTrack> = {};
    Object.values(timeMap).filter((trackArray) => (trackArray as WebinarTrack[]).length >= 1).flat(1).filter((track) => !!track)
      .forEach((track: WebinarTrack) => {
        trackEntities[track.id] = track;
      });
    return Object.values(trackEntities).sort((a: WebinarTrack, b: WebinarTrack) => (a.sort) - b.sort);
  },
);

export const selectAllOnDemandWebinars = () => createSelector(
  selectAllWebinars,
  (webinars) => webinars.filter((webinar) => webinar.kind === WebinarType.OnDemand)
    .sort(onDemandWebinarSortFn),
);

export const selectIsWebinarsLoading = (state) => state.webinars.isLoading;

export const selectWebinarTracksMap = () => createSelector(
  selectAllWebinars,
  (webinars) => webinars.map((webinar: Webinar) => webinar.track)
    .filter((it) => it != null)
    .reduce((tracksMap, next) => {
      tracksMap[next.id] = next;
      return tracksMap;
    }, {}) as { [id: number]: WebinarTrack },
);

export const selectWebinarsByKeyword = (keyword, options: any = {}) => {
  const limit = options.limit || undefined;
  const lowerCaseKeyword = keyword.toLowerCase();
  return createSelector(
    selectAllWebinars,
    (webinars: Webinar[]) => (
      webinars
        .filter((webinar) => {
          if (!lowerCaseKeyword) {
            return false;
          }
          const status = Object.values(webinar.translations).find(({ title }) => (
            title?.toLowerCase()?.includes(lowerCaseKeyword)
          ));
          return !!status;
        })
        .slice(0, limit)
    ),
  );
};

export const selectAllSpeakers = () => createSelector(
  selectAllWebinars,
  (webinars) => {
    const list = [];

    webinars.forEach(({ id: webinarId, speakers: speakersData }) => {
      if (speakersData?.length > 0) {
        speakersData.forEach((speaker) => {
          if (!speaker.id || list.find(({ id }) => id === speaker.id)) {
            return;
          }
          list.push({ ...speaker, webinarId });
        });
      }
    });

    return list.sort((a, b) => a?.sort - b?.sort);
  },
);

export const selectSpeakersByKeyword = (keyword, options: any = {}) => {
  const limit = options.limit || undefined;
  const lowerCaseKeyword = keyword.toLowerCase();
  return createSelector(
    selectAllSpeakers(),
    (speakers: WebinarSpeaker[]) => {
      if (!lowerCaseKeyword) {
        return [];
      }
      return (
        speakers
          .filter((speaker) => {
            const status = Object.values(speaker.translations).find((locale) => {
              const { name, company, title } = locale;
              return (
                name?.toLowerCase()?.includes(lowerCaseKeyword)
                || company?.toLowerCase()?.includes(lowerCaseKeyword)
                || title?.toLowerCase()?.includes(lowerCaseKeyword)
              );
            });
            return !!status;
          })
          .slice(0, limit)
      );
    },
  );
};

export const webinarsSlice = createSlice({
  name: 'webinars',
  initialState: webinarsAdapter.getInitialState({ isLoading: true }),
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchWebinars.pending, (state: any) => {
      state.isLoading = true;
    });
    builder.addCase(fetchWebinars.fulfilled, (state: any, action) => {
      webinarsAdapter.upsertMany(state, action.payload?.webinars || []);
      state.isLoading = false;
    });
    builder.addCase(fetchWebinars.rejected, (state: any) => {
      state.isLoading = false;
    });
  },
});

export default webinarsSlice.reducer;
